import { useLayoutEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { gsap, ScrollTrigger, SplitText, MOTION_OK } from '../animations/gsap';

/**
 * Opt-in scroll animations driven by data attributes.
 *
 *   const ref = useRef(null);
 *   useGsapReveal(ref);
 *   <main ref={ref}> ... <h2 data-animate="split-heading">...</h2> ... </main>
 *
 * data-animate values:
 *   fade-up | fade-in | fade-left | fade-right | scale-in
 *   stagger        children rise in as a batch (uses [data-animate-item] children if present,
 *                  otherwise direct children; children added later, e.g. after a fetch, animate too)
 *   split-heading  words rise in. Only use on headings whose text never changes after render.
 *                  Headings with gradient (bg-clip-text) text rise as one block instead.
 *   parallax       gentle scrubbed drift of the image/video inside the element (768px and up).
 *                  data-parallax="10" sets the travel in percent (default 8). The inner media is
 *                  scaled up slightly to hide edges (wrap it in overflow-hidden); add
 *                  data-parallax-self to move the element itself instead.
 *   counter        counts the first number in the text up from 0 (e.g. "150+", "4.9", "12,000").
 * Optional: data-animate-delay="0.2" (seconds).
 *
 * Content is visible if JS fails: nothing is hidden until GSAP runs, and every hide is paired
 * with a once-trigger. Triggers whose start can't be reached by scrolling (content near the end
 * of a short page) play immediately on refresh, so nothing stays hidden.
 * Reduced motion users get no animation. Everything is reverted on unmount and on route change.
 * New [data-animate] nodes (rendered after a fetch) are picked up automatically, and
 * ScrollTrigger is refreshed when images inside the scope load and the page height changed.
 */

const SELECTOR = '[data-animate]';
const START = 'top 92%';
const EASE = 'power3.out';

/** True when the page can't scroll far enough for this trigger to ever start (e.g. last block above the footer). */
const unreachable = (self) => self.start > ScrollTrigger.maxScroll(window) - 2;

const onceTrigger = (trigger) => ({
  trigger,
  start: START,
  once: true,
  // Pinned sections (home page) must be measured before reveal triggers.
  refreshPriority: -1,
  // Never leave content hidden: if the start point can't be reached, just play the reveal.
  onRefresh: (self) => {
    const anim = self.animation;
    if (anim && unreachable(self) && anim.progress() === 0 && !anim.isActive()) anim.play();
  },
});

const delayOf = (el) => {
  const d = parseFloat(el.dataset.animateDelay);
  return Number.isFinite(d) ? d : 0;
};

function formatNumber(value, decimals, useGrouping) {
  return value.toLocaleString('en-IN', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
    useGrouping,
  });
}

function buildCounter(el, context) {
  const original = el.textContent;
  const match = original.match(/^([^\d]*)(\d[\d,]*(?:\.\d+)?)([\s\S]*)$/);
  if (!match) return;
  const [, prefix, numText, suffix] = match;
  const target = parseFloat(numText.replace(/,/g, ''));
  if (!Number.isFinite(target) || target === 0) return;
  const decimals = (numText.split('.')[1] || '').length;
  const useGrouping = numText.includes(',');
  const state = { v: 0 };
  context.add(() => {
    gsap.to(state, {
      v: target,
      duration: target > 100 ? 1.8 : 1.3,
      ease: 'power2.out',
      delay: delayOf(el),
      scrollTrigger: onceTrigger(el),
      onStart: () => {
        el.textContent = `${prefix}0${suffix}`;
      },
      onUpdate: () => {
        el.textContent = `${prefix}${formatNumber(state.v, decimals, useGrouping)}${suffix}`;
      },
      onComplete: () => {
        el.textContent = original;
      },
    });
    return () => {
      el.textContent = original;
    };
  });
}

function buildElement(el, isDesktop, context, isAlive) {
  const type = el.dataset.animate;
  const delay = delayOf(el);
  const base = {
    autoAlpha: 0,
    duration: 0.9,
    ease: EASE,
    delay,
    clearProps: 'transform,opacity,visibility,transition',
    scrollTrigger: onceTrigger(el),
  };
  // CSS transitions (e.g. Tailwind transition-all for hovers) would lag behind every GSAP frame,
  // so they are paused until the reveal finishes (clearProps restores them).
  const pauseTransitions = () => gsap.set(el, { transition: 'none' });

  switch (type) {
    case 'fade-up':
      pauseTransitions();
      gsap.from(el, { ...base, y: 44 });
      break;
    case 'fade-in':
      pauseTransitions();
      gsap.from(el, { ...base, duration: 1.1, ease: 'power1.out' });
      break;
    case 'fade-left':
      pauseTransitions();
      gsap.from(el, { ...base, x: isDesktop ? -56 : -24 });
      break;
    case 'fade-right':
      pauseTransitions();
      gsap.from(el, { ...base, x: isDesktop ? 56 : 24 });
      break;
    case 'scale-in':
      pauseTransitions();
      gsap.from(el, { ...base, scale: 0.92, y: 24 });
      break;
    case 'split-heading': {
      if (el.querySelector('.bg-clip-text, .text-transparent') || el.classList.contains('bg-clip-text')) {
        pauseTransitions();
        gsap.from(el, { ...base, y: 36, duration: 1 });
        break;
      }
      const run = () => {
        const split = SplitText.create(el, { type: 'words', wordsClass: 'gsap-word' });
        gsap.set(el, { autoAlpha: 1 });
        gsap.from(split.words, {
          yPercent: 70,
          autoAlpha: 0,
          duration: 0.8,
          ease: EASE,
          stagger: 0.045,
          delay,
          scrollTrigger: onceTrigger(el),
        });
      };
      if (document.fonts && document.fonts.status === 'loading') {
        // Split after web fonts are ready so word boxes match the final font (max 1.5s wait).
        gsap.set(el, { autoAlpha: 0 });
        Promise.race([document.fonts.ready, new Promise((resolve) => setTimeout(resolve, 1500))]).then(() => {
          if (isAlive()) context.add(run);
        });
      } else {
        run();
      }
      break;
    }
    case 'parallax': {
      if (!isDesktop) break;
      const self = el.hasAttribute('data-parallax-self');
      const media = self || el.matches('img, video') ? null : el.querySelector('img, video');
      const target = media || el;
      const amount = parseFloat(el.dataset.parallax) || 8;
      const inner = target !== el;
      gsap.set(target, { transition: 'none' });
      gsap.fromTo(
        target,
        { yPercent: -amount, scale: inner ? 1 + (amount * 2.2) / 100 : 1 },
        {
          yPercent: amount,
          scale: inner ? 1 + (amount * 2.2) / 100 : 1,
          ease: 'none',
          scrollTrigger: {
            trigger: el,
            start: 'top bottom',
            end: 'bottom top',
            scrub: 0.6,
            refreshPriority: -1,
          },
        }
      );
      break;
    }
    case 'counter':
      buildCounter(el, context);
      break;
    default:
      break;
  }
}

function staggerItems(container) {
  const marked = container.querySelectorAll('[data-animate-item]');
  return marked.length ? Array.from(marked) : Array.from(container.children);
}

export default function useGsapReveal(scopeRef) {
  const { pathname } = useLocation();

  useLayoutEffect(() => {
    const root = scopeRef.current;
    if (!root || typeof window === 'undefined') return undefined;

    let refreshTimer = null;
    let lastHeight = document.documentElement.scrollHeight;
    const scheduleRefresh = () => {
      clearTimeout(refreshTimer);
      refreshTimer = setTimeout(() => {
        const height = document.documentElement.scrollHeight;
        if (height !== lastHeight) {
          lastHeight = height;
          ScrollTrigger.refresh();
        }
      }, 180);
    };

    // load events don't bubble, so listen in the capture phase for lazy images/videos.
    const onMediaLoad = (e) => {
      const tag = e.target && e.target.tagName;
      if (tag === 'IMG' || tag === 'VIDEO' || tag === 'IFRAME') scheduleRefresh();
    };
    root.addEventListener('load', onMediaLoad, true);
    root.addEventListener('loadedmetadata', onMediaLoad, true);

    const mm = gsap.matchMedia();
    mm.add({ motion: MOTION_OK, desktop: '(min-width: 768px)' }, (context) => {
      const { motion, desktop } = context.conditions;
      if (!motion) return undefined;

      let alive = true;
      const seen = new WeakSet();
      const revealed = new WeakSet();

      const scan = () => {
        let added = false;
        context.add(() => {
          root.querySelectorAll(SELECTOR).forEach((el) => {
            if (el.dataset.animate === 'stagger') {
              const fresh = staggerItems(el).filter((item) => !seen.has(item));
              if (!fresh.length) return;
              fresh.forEach((item) => seen.add(item));
              added = true;
              gsap.set(fresh, { autoAlpha: 0, y: 36, transition: 'none' });
              const reveal = (batch) =>
                gsap.to(batch, {
                  autoAlpha: 1,
                  y: 0,
                  duration: 0.8,
                  ease: EASE,
                  stagger: 0.08,
                  delay: delayOf(el),
                  overwrite: true,
                  clearProps: 'transform,opacity,visibility,transition',
                  onStart: () => batch.forEach((item) => revealed.add(item)),
                });
              ScrollTrigger.batch(fresh, {
                start: START,
                once: true,
                interval: 0.08,
                batchMax: 6,
                refreshPriority: -1,
                onEnter: reveal,
                onRefresh: (self) => {
                  if (unreachable(self) && !revealed.has(self.trigger)) reveal([self.trigger]);
                },
              });
              return;
            }
            if (seen.has(el)) return;
            seen.add(el);
            added = true;
            buildElement(el, desktop, context, () => alive);
          });
        });
        return added;
      };

      scan();

      const observer = new MutationObserver((mutations) => {
        if (!mutations.some((m) => m.addedNodes.length)) return;
        if (scan()) scheduleRefresh();
      });
      observer.observe(root, { childList: true, subtree: true });

      return () => {
        alive = false;
        observer.disconnect();
      };
    });

    return () => {
      clearTimeout(refreshTimer);
      root.removeEventListener('load', onMediaLoad, true);
      root.removeEventListener('loadedmetadata', onMediaLoad, true);
      mm.revert();
    };
  }, [scopeRef, pathname]);
}
